import {
  Base64TemplateResponse,
  DataRow,
  UploadValidationState,
  ValidateAddressStreamResponse,
} from '@/types/address';
import { i18n } from '@/plugins/i18n';
import constant from '@/constant';

// Ensures unknown input is treated as a string array only when it is safely typed.
const coerceStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

// Extracts a non-empty trimmed string or returns null for missing/invalid values.
const toStringValue = (value: unknown): string | null => {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

// Normalizes boolean-like values from API payloads where booleans may arrive as strings.
const toBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  return null;
};

// Parses numbers from numeric or formatted string values (e.g. "1,234").
const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const normalized = value.replace(/,/g, '').trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

// Returns the first value that can be safely parsed as a number.
const firstNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

// Returns the first value that can be safely parsed as a boolean.
const firstBoolean = (...values: unknown[]): boolean | null => {
  for (const value of values) {
    const parsed = toBoolean(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

// Picks the first non-empty string-array candidate from multiple payload locations.
const firstNonEmptyStringArray = (...values: unknown[]): string[] => {
  for (const value of values) {
    const candidate = coerceStringArray(value);
    if (candidate.length > 0) return candidate;
  }
  return [];
};

// Picks the first array payload that can be treated as address rows.
const firstAddressRows = (...values: unknown[]): DataRow[] => {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value as DataRow[];
    }
  }
  return [];
};

// Unwraps validation payloads and supports nested JSON in a message field.
const extractValidationPayload = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object') return {};
  const root = value as Record<string, unknown>;

  if (typeof root.message === 'string') {
    try {
      const parsed = JSON.parse(root.message) as unknown;
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Ignore invalid JSON in message and continue with the root payload.
    }
  }
  return root;
};

// Normalizes streamed validation responses into a stable client-side validation state.
const normalizeStreamValidationResponse = (payload: unknown): UploadValidationState => {
  const resolved = extractValidationPayload(payload);
  const data =
    resolved.data && typeof resolved.data === 'object'
      ? (resolved.data as ValidateAddressStreamResponse & Record<string, unknown>)
      : {};
  const isValid = firstBoolean(data.valid);
  const missingHeaders = firstBoolean(data.missingHeaders) ?? false;
  const warningFromPayload = firstNonEmptyStringArray(data.warnings);
  const incorrectColumnWarning = 'incorrectColumnNameError:';
  const templateErrorWarning = String(i18n.global.t('excelUpload.validation.templateError'));
  const minValidWarningPrefix = 'minValidWarning:';
  const minValidWarning = `${minValidWarningPrefix} ${constant.MAILOUT.MIN_ADDRESS_COUNT}`;
  const hasMissingHeaderError = isValid === false && missingHeaders;
  const payloadWarningsWithoutMin = warningFromPayload.filter(
    (warning) => !warning.startsWith(minValidWarningPrefix)
  );
  const warnings = missingHeaders
    ? [incorrectColumnWarning]
    : hasMissingHeaderError
      ? [templateErrorWarning]
      : payloadWarningsWithoutMin;
  const addressRows = firstAddressRows(data.addressRows);

  const dataCounts = data as Record<string, unknown>;
  const resolvedRecord = resolved as Record<string, unknown>;
  const invalidAddressDownloadUrl = toStringValue(resolvedRecord.presignedUrl) ?? '';
  const invalidAddressFileName = toStringValue(resolvedRecord.file) ?? '';
  const resolvedValidCount = firstNumber(dataCounts.validCount, dataCounts.validAddressCount);
  const resolvedInvalidCount = firstNumber(dataCounts.invalidCount, dataCounts.invalidAddressCount);
  const validAddressCount = resolvedValidCount ?? 0;
  const invalidAddressCount = resolvedInvalidCount ?? 0;
  const shouldShowMinWarning =
    !missingHeaders &&
    resolvedValidCount !== null &&
    validAddressCount < constant.MAILOUT.MIN_ADDRESS_COUNT;

  if (shouldShowMinWarning) {
    if (!warnings.includes(minValidWarning)) {
      warnings.push(minValidWarning);
    }
  }

  return {
    warnings,
    validAddressCount,
    invalidAddressCount,
    addressRows: hasMissingHeaderError ? [] : addressRows,
    invalidAddressDownloadUrl,
    invalidAddressFileName,
  };
};

// Converts a base64 payload to a Blob so binary files can be downloaded in the browser.
const base64ToBlob = (base64Data: string, contentType: string): Blob => {
  const binaryString = atob(base64Data);
  const byteNumbers = new Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    byteNumbers[i] = binaryString.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
};

// Type guard that verifies the sample template API payload carries a base64 body.
const isBase64TemplateResponse = (value: unknown): value is Base64TemplateResponse => {
  if (!value || typeof value !== 'object') return false;
  const obj = value as { body?: unknown };
  return typeof obj.body === 'string' && obj.body.length > 0;
};

export { base64ToBlob, isBase64TemplateResponse, normalizeStreamValidationResponse };
